import { Button, Card, Field, Logo, Muted, Screen, Stack, color, space } from '@biopeak/ui';
import { useState, type FormEvent } from 'react';
import { supabase } from '../lib/supabase.ts';
import { ForgotPassword } from './ForgotPassword.tsx';

/**
 * Entrada do painel — SÓ login, sem cadastro.
 *
 * Diferente do app do paciente, aqui não existe "criar conta": o médico é
 * cadastrado por processo administrativo (script SQL, build-prompt §9). Se
 * oferecêssemos autocadastro, qualquer pessoa poderia criar uma conta e pedir
 * para ser vinculada como médico depois — o cadastro precisa nascer de uma
 * ação deliberada de quem administra a clínica, não de um formulário público.
 */
export function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);

  if (forgot) {
    return <ForgotPassword onBack={() => setForgot(false)} />;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (signInError) throw signInError;
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen style={{ justifyContent: 'center', minHeight: '100vh', maxWidth: '420px' }}>
      <Stack gap={space.sm}>
        <Logo variant="lockup" size={40} />
        <Muted>Painel do médico</Muted>
      </Stack>

      <Card>
        <form onSubmit={handleSubmit}>
          <Stack gap={space.md}>
            <Field
              label="E-mail"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <Field
              label="Senha"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            {error && (
              <p role="alert" style={{ color: '#C7332B', fontSize: '15px' }}>
                {error}
              </p>
            )}

            <Button type="submit" fullWidth disabled={busy}>
              {busy ? 'Entrando…' : 'Entrar'}
            </Button>
          </Stack>
        </form>
      </Card>

      <button
        type="button"
        onClick={() => setForgot(true)}
        style={{
          background: 'none',
          border: 'none',
          color: color.accent,
          fontSize: '15px',
          padding: space.sm,
        }}
      >
        Esqueci minha senha
      </button>

      <Muted style={{ fontSize: '13px', textAlign: 'center' }}>
        Acesso restrito a médicos cadastrados. Fale com a administração da
        clínica se você deveria ter acesso e não consegue entrar.
      </Muted>
    </Screen>
  );
}

function messageFor(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  if (/invalid login credentials/i.test(raw)) {
    return 'E-mail ou senha incorretos.';
  }
  if (/rate limit|too many/i.test(raw)) {
    return 'Muitas tentativas. Aguarde um momento e tente de novo.';
  }
  return 'Não foi possível entrar agora. Tente novamente em instantes.';
}
